import { getCurrentInstance, ref } from 'vue';
import { isEmpty } from 'lodash';

interface SocketScope {
    connection: string;
    joinMethod: string;
    joinArgs: unknown[];
    scopeKey: string;
}

interface SignalRSocket {
    connection: string;
    start(options?: { log?: boolean }): Promise<unknown>;
    stop(): Promise<unknown>;
    invoke(methodName: string, ...args: unknown[]): Promise<unknown>;
}

interface AppProxy {
    $auth?: {
        user?: {
            email?: string;
            name?: string;
            [key: string]: unknown;
        } | null;
        session?: unknown;
        role?: string[];
        funeralHomeId?: number | string;
    };
    $route?: {
        name?: string | null;
        params?: Record<string, string | undefined>;
    };
    $socket?: SignalRSocket;
}

const anonPages = [
    'TributeUploadPage',
    'TributeFamilyPage',
    'TributeStoryUpload',
    'TributeVideoContributor',
    'TributeVideoFamily',
];

export function useSignalRScope() {
    const instance = getCurrentInstance();
    const socketScopeKey = ref<string | null>(null);
    const socketJoinPromise = ref<Promise<boolean> | null>(null);

    function getProxy(): AppProxy {
        const proxy = instance?.proxy as AppProxy | undefined;

        if (!proxy) {
            throw new Error('SignalR scope requires a component instance');
        }

        return proxy;
    }

    function getSocketScope(): SocketScope | null {
        const proxy = getProxy();
        const route = proxy.$route;
        const auth = proxy.$auth;

        if (!route) {
            return null;
        }

        const routeName = route.name ? String(route.name) : '';

        if (anonPages.includes(routeName) && isEmpty(auth?.user)) {
            const slug = route.params?.slug;

            return {
                connection: `${import.meta.env.VUE_APP_SIGNALR_HUBS}/viewing`,
                joinMethod: 'JoinSlug',
                joinArgs: [slug],
                scopeKey: `viewing:${slug ?? ''}`,
            };
        }

        if (auth?.user?.email && auth?.session && auth?.user?.name && auth?.role) {
            if (auth.role.includes('SuperAdmin')) {
                return {
                    connection: `${import.meta.env.VUE_APP_SIGNALR_HUBS}/superadmin`,
                    joinMethod: 'JoinApplication',
                    joinArgs: [],
                    scopeKey: 'superadmin',
                };
            }

            if (auth.role.includes('FuneralHome')) {
                const funeralHomeId = auth.funeralHomeId?.toString();

                return {
                    connection: `${import.meta.env.VUE_APP_SIGNALR_HUBS}/funeralhome`,
                    joinMethod: 'JoinApplication',
                    joinArgs: funeralHomeId ? [funeralHomeId] : [],
                    scopeKey: `funeralhome:${funeralHomeId ?? ''}`,
                };
            }

            if (auth.role.includes('Owner') || auth.role.includes('RegionalManager')) {
                const ownerId = auth.user['https://memoryshare.com/owner_id']?.toString() ?? '';

                return {
                    connection: `${import.meta.env.VUE_APP_SIGNALR_HUBS}/owner`,
                    joinMethod: 'JoinApplication',
                    joinArgs: ownerId ? [ownerId] : [],
                    scopeKey: `owner:${ownerId}`,
                };
            }
        }

        return null;
    }

    async function joinSocketScope(forceRejoin = false): Promise<boolean> {
        if (socketJoinPromise.value) {
            return socketJoinPromise.value;
        }

        const proxy = getProxy();
        const socket = proxy.$socket;

        if (!socket) {
            throw new Error('SignalR socket unavailable');
        }

        const scope = getSocketScope();

        if (!scope) {
            return false;
        }

        const needsScopeChange = socketScopeKey.value !== scope.scopeKey;
        const shouldJoin = forceRejoin || needsScopeChange || socketScopeKey.value == null;

        socketJoinPromise.value = (async () => {
            if (!shouldJoin) {
                return true;
            }

            if (needsScopeChange && socketScopeKey.value) {
                try {
                    await socket.stop();
                } catch (error) {
                    console.warn('failed to stop previous socket connection before reconnecting', error);
                }
            }

            if (needsScopeChange || socketScopeKey.value == null) {
                socket.connection = scope.connection;
                await socket.start({
                    log: false,
                });
            }

            await socket.invoke(scope.joinMethod, ...scope.joinArgs);
            socketScopeKey.value = scope.scopeKey;

            return true;
        })();

        try {
            return await socketJoinPromise.value;
        } finally {
            socketJoinPromise.value = null;
        }
    }

    return {
        getSocketScope,
        joinSocketScope,
        socketScopeKey,
    };
}
